import { observer } from "mobx-react-lite";
import { Message, Table } from "semantic-ui-react";
import MyPaging from "../../app/common/form/MyPaging";
import { SavePurchaseFormValues } from "../../app/models/inventory";
import { FormikErrors } from "formik";
import { useTranslation } from "react-i18next";
import { useStore } from "../../app/stores/store";
import MyIconDecimalInput from "../../app/common/form/MyIconDecimalInput";
import { useEffect } from "react";
import { PagingParams } from "../../app/models/pagination";

type Props = {
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<{
        purchases: SavePurchaseFormValues[];
        vendorId: undefined;
        poNumber: undefined;
    }>>;
    values: {
        purchases: SavePurchaseFormValues[];
        vendorId: undefined;
        poNumber: undefined;
    };
    // listOnClick: (value: number) => void;
    setLoadCounter: (val: number) => void;
    loadCounter: number;
}

export default observer(function InventoryPurchasesTable({ setFieldValue, values, setLoadCounter, loadCounter }: Props) {
    const { t } = useTranslation(["common", "inventory"]);
    const { inventoryPurchasesStore } = useStore();
    const { 
        getPurchases, 
        purchasesPagination, 
        purchasesPagingParams,
        setPurchasesPagingParams,
        resetPurchasesRegistry,
        loadPurchases,
        
    } = inventoryPurchasesStore;

    function listOnClick(value: number) {
        if (value > 0 && purchasesPagination && value < purchasesPagination?.totalPages + 1) {
            setPurchasesPagingParams(new PagingParams(value));
            resetPurchasesRegistry();
            loadPurchases();
            setLoadCounter(loadCounter + 1);
        }
    }
    useEffect(
        () => {
            setFieldValue('purchases', getPurchases.map((purchase) => ({
                id: purchase.id,
                partNumber: purchase.partNumber,
                description: purchase.partDescription,
                unitCost: purchase.unitCost ?? 0,
                quantityPurchased: purchase.quantityPurchased ?? 0,
                locationId: purchase.locationId,
                isDirty: false
            } as SavePurchaseFormValues)))
        }, 
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [loadCounter]
    );

    return (
        <Table striped unstackable className="dnxTable">
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>
                        {t("part_number", { ns: "reports" })}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        {t("pops.part_desc", { ns: "tickets" })}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        {`${t("unitCost", { ns: "inventory" })}`}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        {`${t("quantityPurchased", { ns: "inventory" })}`}
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        {`${t("location", { ns: "inventory" })}`}
                    </Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {getPurchases && getPurchases.length ? (
                    getPurchases.map((purchase, index) => {
                        const purchaseInValues = values.purchases[index] ?? values.purchases.find(p => p.partCode === purchase.partNumber);
                        const unitCost = purchaseInValues?.unitCost ?? 0;
                        const quantityPurchased = purchaseInValues?.quantityPurchased ?? 0;

                        return (
                            <Table.Row key={purchase.id}>
                                <Table.Cell data-label={`${t("part_number", { ns: "reports" })}:`}>
                                    {purchase.partNumber}
                                </Table.Cell>
                                <Table.Cell
                                    data-label={`${t("pops.part_desc", { ns: "tickets" })}:`}
                                >
                                    {purchase.partDescription}
                                </Table.Cell>

                                <Table.Cell
                                    data-label={`${t("unitCost", { ns: "inventory" })}:`}
                                >
                                    <MyIconDecimalInput
                                        icon="dollar sign"
                                        iconPosition="left"
                                        placeholder="0.00"
                                        name={`unitCost_${purchase.id}`}
                                        value={unitCost}
                                        onChange={(d) => {
                                            if (d) {
                                                const cpyPurchases = values.purchases.slice();
                                                cpyPurchases[index].unitCost = d;
                                                cpyPurchases[index].isDirty = true;
                                                setFieldValue("purchases", cpyPurchases);
                                            }
                                        }}
                                        maxLength={500}
                                    />
                                </Table.Cell>

                                <Table.Cell
                                    data-label={`${t("quantityPurchased", { ns: "inventory" })}:`}
                                >
                                    <MyIconDecimalInput
                                        icon="boxes"
                                        iconPosition="left"
                                        placeholder="0.00"
                                        name={`quantity_${purchase.id}`}
                                        value={quantityPurchased}
                                        onChange={(d) => {
                                            if (d) {
                                                const cpyPurchases = values.purchases.slice();
                                                cpyPurchases[index].quantityPurchased = d;
                                                cpyPurchases[index].isDirty = true;
                                                setFieldValue("purchases", cpyPurchases);
                                            }
                                        }}
                                        maxLength={500}
                                    />
                                </Table.Cell>

                                <Table.Cell
                                    data-label={`${t("location", { ns: "inventory" })}:`}
                                >
                                    {purchase.location}
                                </Table.Cell>
                            </Table.Row>
                        )
                    })
                ) : (
                    <Table.Row key={"noItems"}>
                        <Table.Cell width="5" colSpan={5}>
                            <Message
                                color="yellow"
                                className="textAlignCenter fontStyleItalic"
                            >
                                {t("grid_noItems", { ns: "common" })}
                            </Message>
                        </Table.Cell>
                    </Table.Row>
                )}
            </Table.Body>
            <Table.Footer>
                <Table.Row>
                    <Table.Cell colSpan="5">
                        <MyPaging
                            itemCount={purchasesPagination ? purchasesPagination.totalItems : 10}
                            pageSize={purchasesPagingParams.pageSize}
                            currentPage={purchasesPagination ? purchasesPagination.currentPage : 1}
                            onClick={listOnClick}
                            onChange={listOnClick}
                            floated="right"
                        ></MyPaging>
                    </Table.Cell>
                </Table.Row>
            </Table.Footer>
        </Table>
    );
})